import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "../lib/apiClient";
import { useAuth } from "./AuthContext";
import { store } from "../lib/storage";

const GUEST_KEY = "ulume_guest_cart";
const readGuest = () => { try { return JSON.parse(store.get(GUEST_KEY) || "[]"); } catch { return []; } };
const writeGuest = (l) => store.set(GUEST_KEY, JSON.stringify(l));

export const EMPTY_CART = { items: [], saved: [], coupon: null, coupon_error: null, count: 0, subtotal: 0, mrp_total: 0, savings: 0, discount: 0, delivery_fee: 0, total: 0, free_delivery_above: 0 };

const CartCtx = createContext({ cart: EMPTY_CART, count: 0, loading: false, refresh: async () => {}, addItem: async () => {}, setQty: async () => {},
  remove: async () => {}, saveForLater: async () => {}, applyCoupon: async () => {}, removeCoupon: async () => {}, wishIds: new Set(), toggleWish: async () => {}, isGuest: true });

function guestTotals(lines) {
  const subtotal = lines.reduce((n, l) => n + l.price * l.quantity, 0);
  const mrp = lines.reduce((n, l) => n + (l.mrp || l.price) * l.quantity, 0);
  return { subtotal, mrp_total: mrp, savings: mrp - subtotal, discount: 0, delivery_fee: 0, total: subtotal, count: lines.reduce((n, l) => n + l.quantity, 0) };
}

/** Cart + wishlist state. Signed-out shoppers get a browser-stored cart that merges into their account on sign-in. */
export function CartProvider({ children }) {
  const { user, loading: authLoading } = useAuth();
  const [cart, setCart] = useState(null);
  const [wishIds, setWishIds] = useState(new Set());

  const loadGuest = useCallback(async () => {
    const g = readGuest();
    if (!g.length) { setCart(EMPTY_CART); return; }
    try {
      const { data } = await api.get("/products/batch", { params: { ids: g.map(x => x.product_id).join(",") }, silent: true });
      const byId = Object.fromEntries(data.map(p => [p.id, p]));
      const active = [], saved = [];
      for (const it of g) {
        const p = byId[it.product_id]; if (!p) continue;
        const line = { ...p, quantity: Math.min(it.quantity, Math.max(p.stock, 1)), saved: !!it.saved };
        (line.saved ? saved : active).push(line);
      }
      setCart({ ...EMPTY_CART, items: active, saved, ...guestTotals(active) });
    } catch { setCart(c => c || EMPTY_CART); }
  }, []);

  const refresh = useCallback(async () => {
    if (authLoading) return;
    if (!user) { setWishIds(new Set()); await loadGuest(); return; }
    const g = readGuest();
    if (g.length) {
      try {
        await api.post("/cart/merge", { items: g.map(({ product_id, quantity }) => ({ product_id, quantity })) }, { silent: true });
        // items the guest had saved for later should stay saved
        await Promise.all(g.filter(x => x.saved).map(x => api.put(`/cart/${x.product_id}/saved`, { saved: true }, { silent: true }).catch(() => {})));
        writeGuest([]);
      } catch { /* keep guest cart for next time */ }
    }
    try {
      const [c, w] = await Promise.all([api.get("/cart", { silent: true }), api.get("/wishlist", { silent: true })]);
      setCart(c.data); setWishIds(new Set(w.data.map(p => p.id)));
    } catch { setCart(c => c || EMPTY_CART); }
  }, [user, authLoading, loadGuest]);

  useEffect(() => { refresh(); }, [refresh]);

  const guestUpdate = async (fn) => { writeGuest(fn(readGuest())); await loadGuest(); };

  const addItem = useCallback(async (product, qty = 1) => {
    if (product.stock <= 0) throw new Error("This item is out of stock");
    if (user) { await api.post("/cart", { product_id: product.id, quantity: qty }); await refresh(); return; }
    await guestUpdate(list => {
      const found = list.find(x => x.product_id === product.id);
      if (found) { found.quantity = Math.min(found.quantity + qty, product.stock); found.saved = false; return list; }
      return [...list, { product_id: product.id, quantity: Math.min(qty, product.stock), saved: false }];
    });
  }, [user, refresh]); // eslint-disable-line react-hooks/exhaustive-deps

  const setQty = useCallback(async (pid, qty) => {
    if (user) { await api.put(`/cart/${pid}`, { quantity: qty }); await refresh(); return; }
    await guestUpdate(list => list.map(x => x.product_id === pid ? { ...x, quantity: qty } : x));
  }, [user, refresh]); // eslint-disable-line react-hooks/exhaustive-deps

  const remove = useCallback(async (pid) => {
    if (user) { await api.delete(`/cart/${pid}`); await refresh(); return; }
    await guestUpdate(list => list.filter(x => x.product_id !== pid));
  }, [user, refresh]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveForLater = useCallback(async (pid, saved) => {
    if (user) { await api.put(`/cart/${pid}/saved`, { saved }); await refresh(); return; }
    await guestUpdate(list => list.map(x => x.product_id === pid ? { ...x, saved } : x));
  }, [user, refresh]); // eslint-disable-line react-hooks/exhaustive-deps

  const applyCoupon = useCallback(async (code) => { const { data } = await api.post("/cart/coupon", { code }, { silent: true }); await refresh(); return data; }, [refresh]);
  const removeCoupon = useCallback(async () => { await api.delete("/cart/coupon"); await refresh(); }, [refresh]);

  const toggleWish = useCallback(async (pid) => {
    const { data } = await api.post(`/wishlist/${pid}`);
    setWishIds(new Set(data.product_ids));
    return data.product_ids.includes(pid);
  }, []);

  const value = useMemo(() => ({ cart: cart || EMPTY_CART, ready: cart !== null, count: cart?.count || 0, refresh, addItem, setQty, remove, saveForLater,
    applyCoupon, removeCoupon, wishIds, toggleWish, isGuest: !user }),
    [cart, refresh, addItem, setQty, remove, saveForLater, applyCoupon, removeCoupon, wishIds, toggleWish, user]);
  return <CartCtx.Provider value={value}>{children}</CartCtx.Provider>;
}

export const useCart = () => useContext(CartCtx);
